import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Pagination,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PosterCard, type PosterItem } from './PosterCard';
import type { ShareAssetKind } from '../../shared/components/ShareAssetEmailDialog';

// ---------------------------------------------------------------------------
// PaginatedPosterGrid
//
// Drop-in grid that renders a list of `PosterItem`s as a responsive
// 2/3/4/5-column grid, paginates them, and supports horizontal swipe
// gestures on touch devices to step pages.
//
// Used by:
//   • EDM templates catalogue (Project EDM page)
//   • Upcoming Projects / Commercial listing pages
//
// `resetKey` is a string the parent can change to force the page index
// back to 1 (e.g. when the user changes the active tab or search).
// ---------------------------------------------------------------------------

const PAGE_SIZE_MOBILE = 4;
const PAGE_SIZE_DESKTOP = 10;
const SWIPE_THRESHOLD_PX = 50;

type PaginatedPosterGridBaseProps = {
  /** Shown when `items` is empty (after parent filtering). */
  emptyLabel?: string;
  /** Bumping this value resets pagination to page 1. */
  resetKey?: string;
};

export type PaginatedPosterGridProps<T extends { id: string } = PosterItem> =
  PaginatedPosterGridBaseProps & {
    items: T[];
    /** Custom card renderer. Defaults to `PosterCard` when omitted. */
    renderItem?: (item: T) => React.ReactNode;
    onPreview?: (item: PosterItem) => void;
    onDownloadClick?: (item: PosterItem) => void;
    shareKind?: ShareAssetKind;
  };

type PaginatedPosterGridDefaultProps = PaginatedPosterGridBaseProps & {
  items: PosterItem[];
  renderItem?: undefined;
  onPreview?: (item: PosterItem) => void;
  onDownloadClick?: (item: PosterItem) => void;
  shareKind?: ShareAssetKind;
};

type PaginatedPosterGridCustomProps<T extends { id: string }> =
  PaginatedPosterGridBaseProps & {
    items: T[];
    renderItem: (item: T) => React.ReactNode;
    onPreview?: (item: PosterItem) => void;
    onDownloadClick?: (item: PosterItem) => void;
    shareKind?: ShareAssetKind;
  };

export function PaginatedPosterGrid(props: PaginatedPosterGridDefaultProps): React.ReactElement;
export function PaginatedPosterGrid<T extends { id: string }>(
  props: PaginatedPosterGridCustomProps<T>,
): React.ReactElement;
export function PaginatedPosterGrid<T extends { id: string }>({
  items,
  renderItem,
  emptyLabel = 'No items to display.',
  resetKey,
  onPreview,
  onDownloadClick,
  shareKind = 'edm',
}: PaginatedPosterGridDefaultProps | PaginatedPosterGridCustomProps<T>) {
  const theme = useTheme();
  // `up('sm')` matches tablet and larger; phones (xs) fall through to
  // the smaller mobile page size so the grid stays a comfortable two
  // rows of two cards on a single screen.
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const pageSize = isDesktop ? PAGE_SIZE_DESKTOP : PAGE_SIZE_MOBILE;

  const [page, setPage] = useState(1);

  // Reset to page 1 whenever the upstream filter narrows the dataset
  // (parent signals via `resetKey`) or the page size changes (e.g.
  // device rotation crossing the breakpoint).
  useEffect(() => {
    setPage(1);
  }, [resetKey, pageSize]);

  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  // Clamp the page in case `items` shrank between renders.
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;
  const visible = items.slice(start, start + pageSize);

  // ── Mobile swipe-to-paginate ─────────────────────────────────────
  // Track the initial touch point so we can classify the gesture on
  // release. We only treat it as a page-flip when (1) the dominant
  // axis is horizontal (so vertical scrolls are never hijacked), and
  // (2) the horizontal travel clears a small threshold (so accidental
  // taps and short drifts don't change the page).
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (pageCount <= 1) return;
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const startPt = touchStartRef.current;
    touchStartRef.current = null;
    if (!startPt || pageCount <= 1) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - startPt.x;
    const dy = t.clientY - startPt.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx < 0) {
      setPage((p) => Math.min(pageCount, p + 1));
    } else {
      setPage((p) => Math.max(1, p - 1));
    }
  };

  if (items.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ color: 'var(--color-text-muted)', py: 4, textAlign: 'center' }}
      >
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <>
      <Box
        // Swipe gestures on the grid step the pagination on mobile;
        // see `handleTouchStart`/`handleTouchEnd` above for the
        // threshold and horizontal-dominance check.
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
            lg: 'repeat(5, minmax(0, 1fr))',
          },
          alignItems: 'stretch',
          gap: { xs: 1.25, sm: 1.5 },
          // Hint to the browser that horizontal swipes belong to us
          // while vertical pans still scroll the page normally.
          touchAction: 'pan-y',
        }}
      >
        {renderItem
          ? visible.map((item) => (
              <React.Fragment key={item.id}>{renderItem(item as T)}</React.Fragment>
            ))
          : (visible as PosterItem[]).map((item) => (
              <PosterCard
                key={item.id}
                item={item}
                onPreview={onPreview}
                onDownloadClick={onDownloadClick}
                shareKind={shareKind}
              />
            ))}
      </Box>

      {pageCount > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: { xs: 2, sm: 2.5 } }}>
          <Pagination
            count={pageCount}
            page={safePage}
            onChange={(_e, p) => setPage(p)}
            shape="rounded"
            siblingCount={1}
            sx={{
              '& .MuiPaginationItem-root': {
                color: 'var(--color-text-strong)',
                borderColor: 'var(--color-border-strong)',
                fontWeight: 600,
                '&.Mui-selected': {
                  bgcolor: 'var(--color-accent)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'var(--color-accent-strong)' },
                },
                '&:hover': { bgcolor: 'var(--color-accent-soft)' },
              },
            }}
          />
        </Stack>
      )}
    </>
  );
}
